import type { AnimationI, AnySceneObjectT, CanvasI, TimelineI } from "tweenly"
import saveAs from "file-saver"
import JSONfn from "json-fn"
import { defaultTimeline } from "../data/defaults/timeline.types.defaults"
import { FileResponse, getFile } from "../helpers/ajax.helpers"
import { getMetaTag, readFile } from "../helpers/file.helpers"
import { FileProcessor } from "../helpers/fileProcessor.helpers"
import { selectUndoable } from "../helpers/selector.helpers"
import { AssetT } from "../store/slices/assets.slice"
import { setLoading, setPercentLoading } from "../store/slices/loading.slice"
import { selectObjects } from "../store/slices/objects.slice"
import { selectAllAnimations } from "../store/slices/objects.slice/animation/selectAllAnimations"
import { useCanvas } from "./useCanvas"
import { useAppDispatch, useAppSelector, useAppStore } from "./useRedux"

// const GSAPlibrary: string = '<script src="https://cdnjs.cloudflare.com/ajax/libs/gsap/3.9.1/gsap.min.js"></script>'
// const jQueryLibrary: string = `<script src="https://code.jquery.com/jquery-3.6.0.min.js" integrity="sha256-/xUj+3OJU5yExlq6GSYGSHk7tPXikynS7ogEvDej/m4=" crossorigin="anonymous"></script>`

const useHTMLFileContent = () => {
    const dispatch = useAppDispatch()

    const assets: AssetT[] = useAppSelector((state) => state.assets.data)
    const graphic = useAppSelector((state) => state.graphic.data)
    const { canvasResolution: canvas } = useCanvas()
    const store = useAppStore()
    const getScene = (): { allObjects: AnySceneObjectT[]; animations: AnimationI[] } => {
        const allObjects = selectObjects(selectUndoable(store.getState()).objects)
        return { allObjects, animations: selectAllAnimations(allObjects) }
    }

    const timeline: TimelineI = useAppSelector((state) => state.timeline.value)

    const processFile = (
        basePath: string = window.location.origin,
        files: FileResponse[] = [],
        freeVersionHtml: boolean = false
    ) => {
        const isFreeVersionHtml = freeVersionHtml
        const { allObjects, animations } = getScene()
        const fileProcessor = new FileProcessor(
            allObjects,
            animations,
            assets,
            timeline,
            canvas,
            graphic,
            isFreeVersionHtml
        )
        return fileProcessor.processFile(basePath, files, isFreeVersionHtml)
    }

    const loadingCallback = (value: number) => {
        dispatch(setPercentLoading(value))
    }

    const processZip = async () => {
        const { allObjects, animations } = getScene()
        const fileProcessor = new FileProcessor(
            allObjects,
            animations,
            assets,
            timeline,
            canvas,
            graphic,
            false // for .zip is allowed full version of APIs and code triggers
        )
        return fileProcessor.processZip(loadingCallback)
    }

    const downloadFile = async (id: string, graphicName: string) => {
        dispatch(setLoading(true))
        const data = await getFile(id)
        saveAs(data, `${graphicName}.html`)
        dispatch(setLoading(false))
    }

    const downloadZip = async (id: string, graphicName: string) => {
        const file = await readFile(await getFile(id))

        const assets: AssetT[] = JSONfn.parse(getMetaTag(file, "assets"))
        const canvas: CanvasI = JSONfn.parse(getMetaTag(file, "canvas"))
        const allObjects: AnySceneObjectT[] = JSONfn.parse(getMetaTag(file, "objects"))

        let animationState = getMetaTag(file, "animations")
        let animations: AnimationI[] =
            animationState.length > 0
                ? JSONfn.parse(animationState)
                : selectAllAnimations(allObjects)

        const timelineState = getMetaTag(file, "timeline")
        const timeline: TimelineI =
            timelineState.length > 0 ? JSONfn.parse(timelineState) : defaultTimeline

        const fileProcessor = new FileProcessor(
            allObjects,
            animations,
            assets,
            timeline,
            canvas,
            graphic,
            false // for .zip is allowed full version of APIs and code triggers
        )

        dispatch(setLoading(true))
        saveAs(await fileProcessor.processZip(loadingCallback), `${graphicName}.zip`)
        dispatch(setLoading(false))
    }

    return { processFile, processZip, downloadFile, downloadZip }
}

export default useHTMLFileContent
